import { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ScheduleSection({ user, platforms }) {
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [content, setContent] = useState('');
  const [scheduledPosts, setScheduledPosts] = useState([
    {
      id: 1,
      platform: 'Instagram',
      content: 'Sample scheduled post for tomorrow',
      scheduledFor: '2025-11-14 10:00 AM',
      status: 'pending'
    },
    {
      id: 2,
      platform: 'Twitter',
      content: 'Another scheduled post example',
      scheduledFor: '2025-11-15 2:00 PM',
      status: 'pending'
    }
  ]);

  const schedulePost = async () => {
    if (!selectedDate || !selectedTime || !selectedPlatform || !content) {
      alert('Please fill all fields');
      return;
    }

    const newPost = {
      id: Date.now(),
      platform: selectedPlatform,
      content: content,
      scheduledFor: `${selectedDate} ${selectedTime}`,
      status: 'pending'
    };

    setScheduledPosts([...scheduledPosts, newPost]);
    
    // Reset form
    setSelectedDate('');
    setSelectedTime('');
    setSelectedPlatform('');
    setContent('');
    
    alert('Post scheduled successfully!');
  };

  const cancelSchedule = (id) => {
    setScheduledPosts(scheduledPosts.filter(post => post.id !== id));
    alert('Scheduled post cancelled');
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold gradient-text mb-2">Content Scheduling</h2>
        <p className="style={{ color: 'var(--text-muted)' }}">Schedule your posts for future publishing</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Schedule Form */}
        <div className="theme-card border rounded-xl p-6">
          <h3 className="text-xl font-bold accent-text mb-4">📅 Schedule New Post</h3>
          
          <div className="space-y-4">
            {/* Platform */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Platform</label>
              <select
                value={selectedPlatform}
                onChange={(e) => setSelectedPlatform(e.target.value)}
                className="w-full px-4 py-3 theme-card border text-white rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
              >
                <option value="">Select platform...</option>
                {platforms.map((platform) => (
                  <option key={platform} value={platform}>{platform}</option>
                ))}
              </select>
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Date</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-4 py-3 theme-card border text-white rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
              />
            </div>

            {/* Time */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Time</label>
              <input
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                className="w-full px-4 py-3 theme-card border text-white rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Content</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows="4"
                className="w-full px-4 py-3 theme-card border text-white rounded-lg focus:ring-2 focus:ring-orange-500 outline-none"
                placeholder="Enter your post content..."
              />
            </div>

            <button
              onClick={schedulePost}
              className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-amber-600 transition btn-glow"
            >
              📅 Schedule Post
            </button>
          </div>
        </div>

        {/* Scheduled Posts List */}
        <div className="theme-card border rounded-xl p-6">
          <h3 className="text-xl font-bold accent-text mb-4">📝 Scheduled Posts ({scheduledPosts.length})</h3>
          
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {scheduledPosts.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-5xl mb-3">📅</div>
                <p className="style={{ color: 'var(--text-muted)' }}">No scheduled posts yet</p>
              </div>
            ) : (
              scheduledPosts.map((post) => (
                <div
                  key={post.id}
                  className="theme-card border rounded-lg p-4 hover:border-orange-500/50 transition"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="inline-block px-3 py-1 bg-orange-500/20 text-orange-400 text-xs font-medium rounded-full mb-2">
                        {post.platform}
                      </span>
                      <p className="text-sm text-gray-300">{post.content}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-700">
                    <span className="text-xs text-gray-500">
                      🕒 {post.scheduledFor}
                    </span>
                    <button
                      onClick={() => cancelSchedule(post.id)}
                      className="px-3 py-1 text-xs text-red-400 border border-red-500 rounded hover:bg-red-500/20 transition"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Calendar View Placeholder */}
      <div className="mt-6 theme-card border rounded-xl p-6">
        <h3 className="text-xl font-bold accent-text mb-4">📅 Calendar View</h3>
        <div className="text-center py-12 border-2 border-dashed border-gray-700 rounded-xl">
          <div className="text-5xl mb-3">📅</div>
          <p className="style={{ color: 'var(--text-muted)' }}">Calendar view coming soon!</p>
          <p className="text-sm text-gray-500 mt-2">Visual calendar to manage all scheduled posts</p>
        </div>
      </div>
    </div>
  );
}

export default ScheduleSection;
